import { fs, path } from '../../../lib/cep/node';
import {
  findDesktopAppDataFilePath,
  getDesktopAppDataFilePathForWrite,
  getExchangeDataFilePath,
  getExchangeDataFolderPath,
} from '../folder.util';

type JsonRecord = Record<string, unknown>;

/**
 * Only synchronous `fs` here, and nothing newer than Node 12: CEP ships an old
 * Node and analytics must never be the reason the installer fails.
 */
const readJsonFile = (
  filePath: string
): { ok: true; data: JsonRecord } | { ok: false; exists: boolean } => {
  try {
    if (!fs.existsSync(filePath)) {
      return { ok: false, exists: false };
    }

    const parsed: unknown = JSON.parse(fs.readFileSync(filePath, 'utf-8'));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, exists: true };
    }

    return { ok: true, data: parsed as JsonRecord };
  } catch (error) {
    console.warn(`[analytics] Failed to read ${filePath}`, error);
    return { ok: false, exists: true };
  }
};

const writeJsonFile = (filePath: string, data: JsonRecord): boolean => {
  try {
    const folderPath = path.dirname(filePath);
    if (!fs.existsSync(folderPath)) {
      fs.mkdirSync(folderPath, { recursive: true });
    }
    fs.writeFileSync(filePath, JSON.stringify(data, null, 2), 'utf-8');
    return true;
  } catch (error) {
    console.warn(`[analytics] Failed to write ${filePath}`, error);
    return false;
  }
};

/* -------------------------------------------------------------------------- */
/*                       Desktop app store (autocut_data.json)                */
/* -------------------------------------------------------------------------- */

/**
 * The desktop app stores every value JSON-encoded, so a plain uuid is written as
 * `"\"<uuid>\""`. Mirrors `parseCookieString` in the electron app.
 */
const decodeDesktopValue = (rawValue: unknown): string | undefined => {
  if (typeof rawValue !== 'string' || !rawValue) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);
    return typeof parsed === 'string' && parsed ? parsed : undefined;
  } catch {
    return rawValue;
  }
};

export type DesktopAppSnapshot = {
  /** Anonymous PostHog id the desktop app uses, when it has one. */
  distinctId?: string;
  /**
   * `appUUID` is written by the desktop app itself and never by us, so it is the
   * only trustworthy "AutoCut is really installed" signal — the data file alone
   * is not, because we create it when seeding the id.
   */
  isDesktopAppInstalled: boolean;
  fileExists: boolean;
};

export const readDesktopAppSnapshot = (): DesktopAppSnapshot => {
  const filePath = findDesktopAppDataFilePath();
  if (!filePath) {
    return { isDesktopAppInstalled: false, fileExists: false };
  }

  const result = readJsonFile(filePath);
  if (!result.ok) {
    return { isDesktopAppInstalled: false, fileExists: true };
  }

  return {
    distinctId: decodeDesktopValue(result.data['posthogDistinctId']),
    isDesktopAppInstalled: Boolean(decodeDesktopValue(result.data['appUUID'])),
    fileExists: true,
  };
};

/**
 * Seed a value into the desktop app's store, but only when it is absent —
 * `initCookies()` in the electron app does the same check, so an id written here
 * is adopted verbatim on the app's first run and both sides report the same
 * PostHog person.
 *
 * Two deliberate refusals to write:
 * - the file exists but does not parse: never clobber the app's own data, it
 *   resets the file itself when it is corrupt;
 * - the key is already set: the app may be running right now, and it rewrites
 *   the whole file from an in-memory snapshot, so our write would be lost
 *   anyway (which is also why our own markers live in a separate file).
 */
export const seedDesktopAppCookieIfAbsent = (
  name: string,
  plainValue: string
): 'seeded' | 'already-set' | 'skipped' => {
  try {
    const existingFilePath = findDesktopAppDataFilePath();
    const filePath = existingFilePath ?? getDesktopAppDataFilePathForWrite();
    const result = readJsonFile(filePath);

    if (!result.ok) {
      if (result.exists) {
        return 'skipped';
      }
      return writeJsonFile(filePath, { [name]: JSON.stringify(plainValue) })
        ? 'seeded'
        : 'skipped';
    }

    if (decodeDesktopValue(result.data[name])) {
      return 'already-set';
    }

    return writeJsonFile(filePath, {
      ...result.data,
      [name]: JSON.stringify(plainValue),
    })
      ? 'seeded'
      : 'skipped';
  } catch (error) {
    console.warn('[analytics] seedDesktopAppCookieIfAbsent failed', error);
    return 'skipped';
  }
};

/* -------------------------------------------------------------------------- */
/*                        Our own store (analytics.json)                      */
/* -------------------------------------------------------------------------- */

export type ExchangeStore = {
  posthogDistinctId?: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
  openCount?: number;
};

export const readExchangeStore = (): ExchangeStore => {
  const filePath = getExchangeDataFilePath();
  const result = readJsonFile(filePath);

  if (!result.ok) {
    if (result.exists) {
      // Unparseable: keep a copy once for debugging, then start fresh rather
      // than treating a corrupt file as "never seen before" forever.
      try {
        fs.renameSync(filePath, `${filePath}.bak`);
      } catch {
        /* best effort */
      }
    }
    return {};
  }

  return result.data as ExchangeStore;
};

export const patchExchangeStore = (patch: Partial<ExchangeStore>): boolean => {
  try {
    if (!fs.existsSync(getExchangeDataFolderPath())) {
      fs.mkdirSync(getExchangeDataFolderPath(), { recursive: true });
    }
    return writeJsonFile(getExchangeDataFilePath(), {
      ...readExchangeStore(),
      ...patch,
    });
  } catch (error) {
    console.warn('[analytics] patchExchangeStore failed', error);
    return false;
  }
};
